// Admin users table — matches docs/mockups/admin-list.html.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { AdminStats, AdminUserListItem } from "../api/admin";
import { getStats, listUsers } from "../api/admin";
import { AddUserForm } from "../components/AddUserForm";
import { Chrome } from "../components/Chrome";

const STATUS_LABELS: Record<number, { label: string; cls: string }> = {
  10: { label: "Active", cls: "text-moss" },
  8: { label: "Pending", cls: "text-terra-deep" },
  9: { label: "Unverified", cls: "text-ink-mute" },
  0: { label: "Deleted", cls: "text-plum" },
};

export function AdminListPage() {
  const [users, setUsers] = useState<AdminUserListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<number | undefined>(undefined);
  const [search, setSearch] = useState("");
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    getStats().then(setStats).catch(() => undefined);
  }, [refreshTick]);

  useEffect(() => {
    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        const result = await listUsers({
          search: search || undefined,
          status: statusFilter,
          page,
          page_size: 25,
        });
        setUsers(result.items);
        setTotal(result.total);
      } catch {
        setUsers([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    }, 240);
    return () => clearTimeout(handle);
  }, [search, statusFilter, page, refreshTick]);

  const pendingCount = stats?.pending_approval ?? 0;
  const filterButtons: Array<{ label: string; value: number | undefined; key: string; badge?: number }> = [
    { label: "All", value: undefined, key: "all" },
    { label: "Active", value: 10, key: "active" },
    { label: "Pending", value: 8, key: "pending", badge: pendingCount > 0 ? pendingCount : undefined },
    { label: "Inactive", value: 9, key: "inactive" },
    { label: "Deleted", value: 0, key: "deleted" },
  ];

  return (
    <>
      <Chrome tag="Admin · Users" />
      <main className="mx-auto max-w-7xl px-7 py-9">
        <div className="flex items-baseline justify-between border-b border-rule pb-6">
          <div className="flex items-baseline gap-5">
            <span className="font-display italic text-3xl text-terra leading-none [font-variation-settings:'opsz'_60,'wght'_300]">
              07
            </span>
            <div>
              <p className="eyebrow eyebrow-accent">Admin</p>
              <h1 className="mt-2 text-5xl tracking-tight">Users</h1>
            </div>
          </div>
          <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-mute">
            Snapshot · {new Date().toLocaleString()}
          </span>
        </div>

        {stats && (
          <div className="grid grid-cols-2 gap-5 my-6 md:grid-cols-5">
            <Stat value={stats.total} label="Total accounts" />
            <Stat value={stats.active} label="Active · status 10" />
            <Stat value={stats.pending_approval} label="Pending · status 8" />
            <Stat value={stats.inactive} label="Unverified · status 9" />
            <Stat value={stats.deleted} label="Soft-deleted · status 0" />
          </div>
        )}

        <div className="flex flex-wrap items-center gap-4 border-b border-rule-soft py-5">
          <input
            type="search"
            placeholder="Search name, username, email…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="field-input max-w-md rounded-full !py-2.5"
          />
          <div className="flex flex-wrap gap-2">
            {filterButtons.map((f) => (
              <button
                key={f.key}
                onClick={() => {
                  setStatusFilter(f.value);
                  setPage(1);
                }}
                className={`rounded-full border px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.14em] transition-colors ${
                  statusFilter === f.value
                    ? "border-ink bg-ink text-paper"
                    : "border-rule text-ink-soft hover:bg-paper-deep"
                }`}
              >
                {f.label}
                {f.badge !== undefined && (
                  <span
                    className={`ml-1.5 rounded-full px-1.5 py-0.5 text-[9px] ${
                      statusFilter === f.value
                        ? "bg-paper text-ink"
                        : "bg-terra/15 text-terra-deep"
                    }`}
                  >
                    {f.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
          <button
            onClick={() => setShowAddForm((v) => !v)}
            className={`ml-auto rounded-full border px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.14em] transition-colors ${
              showAddForm
                ? "border-ink bg-ink text-paper"
                : "border-terra text-terra-deep hover:bg-terra/10"
            }`}
          >
            {showAddForm ? "Close" : "+ Add user"}
          </button>
        </div>

        {showAddForm && (
          <AddUserForm
            onCreated={() => {
              setShowAddForm(false);
              setRefreshTick((t) => t + 1);
            }}
            onCancel={() => setShowAddForm(false)}
          />
        )}

        <div className="mt-6 overflow-hidden rounded-lg border border-rule bg-cream-card">
          {loading ? (
            <div className="grid place-items-center py-12">
              <span className="eyebrow">loading…</span>
            </div>
          ) : users.length === 0 ? (
            <div className="grid place-items-center py-12">
              <span className="eyebrow">No users match those filters.</span>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-paper-deep">
                  <Th>Person</Th>
                  <Th>Email</Th>
                  <Th>Status</Th>
                  <Th>Type</Th>
                  <Th>Joined</Th>
                  <Th className="w-px" />
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const s = STATUS_LABELS[u.status] ?? { label: "?", cls: "" };
                  const isDeleted = u.status === 0;
                  return (
                    <tr
                      key={u.id}
                      className={`border-b border-rule-soft transition-colors hover:bg-paper-deep ${
                        isDeleted ? "opacity-55" : ""
                      }`}
                    >
                      <td className="p-4">
                        <Link to={`/admin/users/${u.id}`} className="hover:text-terra-deep">
                          <div className="font-display text-base tracking-tight [font-variation-settings:'opsz'_22,'SOFT'_30,'WONK'_1,'wght'_500]">
                            {[u.first_name, u.last_name].filter(Boolean).join(" ") || u.username}
                          </div>
                          <div className="font-mono text-xs text-ink-mute">
                            varo.biz/{u.username}
                          </div>
                        </Link>
                      </td>
                      <td className="p-4 text-ink-soft">{u.email}</td>
                      <td className="p-4">
                        <Badge className={s.cls}>
                          <Dot className={s.cls.replace("text-", "bg-")} /> {s.label}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <Badge
                          className={
                            u.type === 1
                              ? "text-terra-deep border-transparent bg-terra/10"
                              : "text-ink-mute"
                          }
                        >
                          {u.type === 1 ? "Super admin" : "User"}
                        </Badge>
                      </td>
                      <td className="p-4 text-ink-soft">
                        {new Date(u.created_at).toLocaleDateString(undefined, {
                          year: "numeric",
                          month: "short",
                        })}
                      </td>
                      <td className="p-4">
                        <Link
                          to={`/admin/users/${u.id}`}
                          className="btn btn-ghost text-xs whitespace-nowrap"
                        >
                          Edit
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          <div className="flex items-center justify-between border-t border-rule-soft px-6 py-5 font-mono text-[11px] uppercase tracking-[0.16em] text-ink-mute">
            <span>
              Showing {(page - 1) * 25 + 1}–{Math.min(page * 25, total)} of {total}
            </span>
            <div className="flex gap-1">
              <button
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
                className="px-2 py-1 disabled:opacity-30"
              >
                ←
              </button>
              <span className="rounded bg-ink px-3 py-1 text-paper">{page}</span>
              <button
                disabled={page * 25 >= total}
                onClick={() => setPage(page + 1)}
                className="px-2 py-1 disabled:opacity-30"
              >
                →
              </button>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}

function Stat({ value, label }: { value: number; label: string }) {
  return (
    <div className="rounded-lg border border-rule bg-cream-card px-6 py-5">
      <div className="font-display text-3xl tracking-tight [font-variation-settings:'opsz'_36,'SOFT'_30,'WONK'_1,'wght'_500]">
        {value.toLocaleString()}
      </div>
      <div className="mt-2 font-mono text-[11px] uppercase tracking-[0.18em] text-ink-mute">
        {label}
      </div>
    </div>
  );
}

function Th({ children, className }: { children?: React.ReactNode; className?: string }) {
  return (
    <th
      className={`border-b border-rule px-4 py-3 text-left font-mono text-[11px] font-medium uppercase tracking-[0.16em] text-ink-mute ${className ?? ""}`}
    >
      {children}
    </th>
  );
}

function Badge({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border border-current px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.16em] ${
        className ?? ""
      }`}
    >
      {children}
    </span>
  );
}

function Dot({ className }: { className?: string }) {
  return <span className={`inline-block size-1.5 rounded-full ${className ?? ""}`} />;
}
